import { Link } from 'react-router-dom'
import { defaultTiers, getTiersForModule, getLowestTierForModule } from '@/data/pricing'
import { MODULE_THEME } from '@/data/modules'
import type { ModuleColorClass } from '@/data/modules'

export interface ModuleTierBadgeProps {
  /** Pricing module ID(s) this maps to (see src/data/pricing.ts). */
  pricingModules: string[]
  /**
   * `hero`: light-on-dark chip for the gradient hero.
   * `card`: compact pill for light card grids.
   */
  variant?: 'hero' | 'card'
  /** Tints the `card` pill to match a module's colour. Defaults to teal. */
  colorClass?: ModuleColorClass
  className?: string
}

const DEFAULT_CARD_CLASSES = 'text-accent-teal bg-teal-50 border-teal-100'

/**
 * Tier-availability badge for a module showcase. Derives "which plans include
 * this" entirely from pricing.ts, so it stays correct under either pricing model
 * (modular `defaultTiers` or legacy all-via-`*` `legacyTiers`).
 */
export default function ModuleTierBadge({
  pricingModules,
  variant = 'card',
  colorClass,
  className = '',
}: ModuleTierBadgeProps) {
  // Always resolve against the modular tier set so the badge shows meaningful
  // per-tier availability ("From Professional"), independent of which pricing
  // model is live (NEW_PRICING_ENABLED). The modular tiers are the source of
  // truth for what each module is bundled into.
  const includingTiers = getTiersForModule(pricingModules, defaultTiers)
  const lowest = getLowestTierForModule(pricingModules, defaultTiers)

  if (!lowest) return null

  // When every modular tier includes the module, it's simply "all plans".
  const onAllPlans = includingTiers.length === defaultTiers.length
  const fromLabel = onAllPlans ? 'All plans' : lowest.displayName

  if (variant === 'card') {
    const cardColors = colorClass ? MODULE_THEME[colorClass].badge : DEFAULT_CARD_CLASSES
    return (
      <span
        className={`inline-flex items-center gap-1.5 text-xs font-semibold border px-2.5 py-1 rounded-full ${cardColors} ${className}`}
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        {onAllPlans ? 'All plans' : `From ${fromLabel}`}
      </span>
    )
  }

  // hero variant: reads on the dark gradient hero.
  return (
    <Link
      to="/pricing"
      className={`inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm text-white no-underline backdrop-blur-sm transition-colors hover:bg-white/15 ${className}`}
    >
      <span className="font-semibold">
        {onAllPlans ? 'Available on all plans' : `Available from ${fromLabel}`}
      </span>
      {!onAllPlans && (
        <span className="flex flex-wrap items-center gap-1.5">
          {includingTiers.map((tier) => (
            <span
              key={tier.slug}
              className="rounded-full bg-white/15 px-2 py-0.5 text-xs font-medium text-white/90"
            >
              {tier.displayName}
            </span>
          ))}
        </span>
      )}
    </Link>
  )
}
